import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  Button,
  Badge,
} from '@vnetwork/ui/primitives';
import { StatusPill } from '@vnetwork/ui/data';
import type { AlarmEvent, Watchpoint } from '@/api/healthcheck';
import { typeMeta, eventStatusTone, severityTone, fmtDateTime } from '../shared';
import { IncidentContext } from './incident-context';

interface Props {
  event: AlarmEvent | null;
  watchpoint?: Watchpoint;
  onClose: () => void;
  onAcknowledge: (id: string) => void;
  onResolve: (id: string) => void;
  busy?: boolean;
}

/**
 * Incident detail — a side-modal whose body adapts to the monitor type (TLS cert,
 * DNS records, elastic breakdown, protocol status/latency). Opened from a row.
 */
export function IncidentDetailDialog({ event, watchpoint, onClose, onAcknowledge, onResolve, busy }: Props) {
  const type = watchpoint?.type ?? 'http';
  const tm = typeMeta(type);
  const Icon = tm.icon;

  return (
    <Dialog open={!!event} onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
              <Icon size={18} />
              {watchpoint?.name ?? 'Incident'}
              <Badge tone="neutral" appearance="subtle" size="sm">{tm.label}</Badge>
            </span>
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          {event && (
            <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)', flexWrap: 'wrap' }}>
                <Badge tone={severityTone(event.severity ?? 'warning')} appearance="subtle" size="sm" style={{ textTransform: 'capitalize' }}>
                  {event.severity ?? 'warning'}
                </Badge>
                <Badge tone={eventStatusTone(event.status)} appearance="subtle" size="sm" style={{ textTransform: 'capitalize' }}>
                  {event.status}
                </Badge>
                {event.alarm_name && <span style={{ fontWeight: 600 }}>{event.alarm_name}</span>}
                <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
                  Triggered {fmtDateTime(event.triggered_at)}
                </span>
                {event.details && 'is_up' in (event.details as Record<string, unknown>) && (
                  <StatusPill tone={(event.details as Record<string, unknown>).is_up ? 'success' : 'danger'}>
                    {(event.details as Record<string, unknown>).is_up ? 'Up' : 'Down'}
                  </StatusPill>
                )}
              </div>

              <section>
                <div style={{ fontWeight: 600, marginBottom: 'var(--vn-space-2)' }}>Probe detail</div>
                <IncidentContext type={type} details={event.details} matchedItems={event.matched_items} />
              </section>

              {(event.acknowledged_by || event.resolved_by) && (
                <section style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
                  {event.acknowledged_by && <div>Acknowledged by {event.acknowledged_by} · {fmtDateTime(event.acknowledged_at)}</div>}
                  {event.resolved_by && <div>Resolved by {event.resolved_by} · {fmtDateTime(event.resolved_at)}</div>}
                </section>
              )}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          {event?.status === 'triggered' && (
            <Button variant="secondary" disabled={busy} onClick={() => onAcknowledge(event.id)}>
              Acknowledge
            </Button>
          )}
          {event && event.status !== 'resolved' && (
            <Button disabled={busy} onClick={() => onResolve(event.id)}>
              Resolve
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
